import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Database,
  ExternalLink,
  FileText,
  Folder,
  HardDrive,
  Inbox,
  Library,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { canManage } from '@/lib/roles'
import {
  DocumentIngestStatus,
  DocumentMappingReviewItem,
  knowledgeWorkspaceApi,
  RelatedWorkspaceDocument,
  WorkspaceDocument,
  WorkspaceAssistantAnswer,
  WorkspaceSystem,
  WorkspaceTree,
} from '@/lib/knowledgeWorkspaceApi'
import { useTranslation } from 'react-i18next'

type WorkspaceTab = 'systems' | 'documents' | 'mapping'
const SMART_RESULTS_KEY = 'seacuredb:workspace-smart-results'

function readSmartResultIds(): string[] {
  if (new URLSearchParams(window.location.search).get('source') !== 'smart') return []
  try {
    const stored = JSON.parse(sessionStorage.getItem(SMART_RESULTS_KEY) ?? '{}')
    return Array.isArray(stored.projectSystemIds)
      ? stored.projectSystemIds.map(String)
      : []
  } catch {
    return []
  }
}

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const order = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / 1024 ** order).toFixed(order > 1 ? 1 : 0)} ${units[order]}`
}

const labelOf = (value: string | null | undefined) => value?.trim() || '—'

function Presence({ yes, children }: { yes: boolean; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
      yes
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
        : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
    }`}>
      {yes ? <Check className="h-3 w-3" /> : <span className="h-3 w-3 text-center">–</span>}
      {children}
    </span>
  )
}

function StatusBadge({
  status,
  autoConfirmed = false,
}: {
  status: WorkspaceSystem['status']
  autoConfirmed?: boolean
}) {
  const { t } = useTranslation()
  if (status === 'confirmed') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
        {autoConfirmed ? <Sparkles className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
        {autoConfirmed ? t('knowledge.status.autoConfirmed') : t('knowledge.status.confirmed')}
      </span>
    )
  }
  if (status === 'suggested') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-violet-700 dark:bg-violet-950/60 dark:text-violet-300">
        <Sparkles className="h-3 w-3" /> {t('knowledge.status.suggested')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-[9px] font-medium uppercase text-slate-400 dark:border-slate-600">
      <CircleDashed className="h-3 w-3" /> {t('knowledge.status.inferred')}
    </span>
  )
}

function SystemInspector({
  system,
  canEdit,
  onFlag,
  flagging,
  onFlagMaker,
  makerFlaggingId,
  relatedDocuments,
  loadingDocuments,
  onOpenDocument,
  onExtractDocuments,
  extractingDocuments,
  onAnalyzeDocuments,
  analyzingDocuments,
  onFlagDocumentLink,
  flaggingDocumentLinkId,
}: {
  system: WorkspaceSystem | null
  canEdit: boolean
  onFlag: (system: WorkspaceSystem) => void
  flagging: boolean
  onFlagMaker: (installation: WorkspaceSystem['installations'][number]) => void
  makerFlaggingId: string | null
  relatedDocuments: RelatedWorkspaceDocument[]
  loadingDocuments: boolean
  onOpenDocument: (document: RelatedWorkspaceDocument) => void
  onExtractDocuments: (documents: RelatedWorkspaceDocument[]) => void
  extractingDocuments: boolean
  onAnalyzeDocuments: (documents: RelatedWorkspaceDocument[]) => void
  analyzingDocuments: boolean
  onFlagDocumentLink: (document: RelatedWorkspaceDocument, linkId: string) => void
  flaggingDocumentLinkId: string | null
}) {
  const { t } = useTranslation()
  if (!system) {
    return (
      <div className="flex h-full min-h-48 flex-col items-center justify-center px-6 text-center text-slate-400">
        <Library className="mb-3 h-8 w-8 text-slate-300 dark:text-slate-600" />
        <p className="text-sm font-medium">{t('knowledge.chooseSystem')}</p>
        <p className="mt-1 text-xs">{t('knowledge.chooseSystemHint')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4 p-4">
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <StatusBadge status={system.status} autoConfirmed={system.auto_confirmed} />
          {system.has_open_flag && (
            <span className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-red-700 dark:bg-red-950/60 dark:text-red-300">
              <AlertTriangle className="h-3 w-3" /> {t('knowledge.flaggedReview')}
            </span>
          )}
          {system.maker_conflict && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
              <Sparkles className="h-3 w-3" /> {t('knowledge.makerCorrected')}
            </span>
          )}
          <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-sky-700 dark:bg-sky-950/60 dark:text-sky-300">
            IACS
          </span>
        </div>
        <h2 className="text-base font-semibold text-slate-900 dark:text-white">{system.type_name}</h2>
        <p className="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300">
          {system.name} ({system.maker ?? t('knowledge.makerUnresolved')})
        </p>
        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
          {system.function_label}
        </p>
        {canEdit && system.status === 'confirmed' && system.canonical_system_id && (
          <Button
            className="mt-3 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30"
            size="sm"
            variant="outline"
            loading={flagging}
            onClick={() => onFlag(system)}
          >
            <AlertTriangle className="h-3.5 w-3.5" /> {t('knowledge.flagGrouping')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-slate-800/70">
          <p className="text-[9px] uppercase text-slate-400">{t('knowledge.vessels')}</p>
          <p className="text-lg font-semibold tabular-nums">{system.vessel_count}</p>
        </div>
        <div className="rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-slate-800/70">
          <p className="text-[9px] uppercase text-slate-400">{t('knowledge.installations')}</p>
          <p className="text-lg font-semibold tabular-nums">{system.installation_count}</p>
        </div>
        <div className="rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-slate-800/70">
          <p className="text-[9px] uppercase text-slate-400">{t('knowledge.evidence')}</p>
          <p className="text-lg font-semibold tabular-nums">{system.coverage.evidence}</p>
        </div>
      </div>

      {(system.vendors.length > 0 || system.models.length > 0) && (
        <div className="space-y-2">
          {system.vendors.length > 0 && (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t('knowledge.vendors')}</p>
              <div className="flex flex-wrap gap-1">
                {system.vendors.slice(0, 8).map(vendor => (
                  <span key={vendor} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {vendor}
                  </span>
                ))}
              </div>
            </div>
          )}
          {system.models.length > 0 && (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t('knowledge.models')}</p>
              <p className="break-words font-mono text-[10px] text-slate-500 dark:text-slate-400">
                {system.models.slice(0, 12).join(' · ')}
              </p>
            </div>
          )}
        </div>
      )}

      {system.aliases.length > 0 && (
        <details className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700">
          <summary className="cursor-pointer text-xs font-medium text-slate-600 dark:text-slate-300">
            {t('knowledge.otherNames', { count: system.aliases.length })}
          </summary>
          <div className="mt-2 max-h-24 space-y-1 overflow-y-auto">
            {system.aliases.slice(0, 12).map(alias => (
              <p key={alias} className="truncate text-[11px] text-slate-500 dark:text-slate-400" title={alias}>
                {alias}
              </p>
            ))}
          </div>
        </details>
      )}

      <section aria-labelledby="dump-documents-heading">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 id="dump-documents-heading" className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {t('knowledge.documentDump')}
            </h3>
            <p className="text-[11px] text-slate-400">
              {loadingDocuments
                ? t('knowledge.findingDocuments')
                : t('knowledge.matchedOriginals', { count: relatedDocuments.length })}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && relatedDocuments.some(document => !document.extraction_status) && (
              <Button
                size="sm"
                variant="outline"
                loading={extractingDocuments}
                onClick={() => onExtractDocuments(relatedDocuments)}
              >
                {t('knowledge.extractInformation')}
              </Button>
            )}
            {canEdit && relatedDocuments.some(document =>
              document.extraction_status === 'completed' && document.analysis_status !== 'completed') && (
              <Button
                size="sm"
                loading={analyzingDocuments}
                onClick={() => onAnalyzeDocuments(relatedDocuments)}
              >
                <Sparkles className="h-3.5 w-3.5" /> {t('knowledge.summarize')}
              </Button>
            )}
          </div>
        </div>
        {loadingDocuments ? (
          <div role="status" className="rounded-lg border border-slate-200 px-3 py-6 text-center text-xs text-slate-400 dark:border-slate-700">
            {t('knowledge.searchingDump')}
          </div>
        ) : relatedDocuments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 px-3 py-6 text-center dark:border-slate-700">
            <FileText className="mx-auto mb-2 h-5 w-5 text-slate-300" />
            <p className="text-xs text-slate-500">{t('knowledge.noMatchedDocument')}</p>
          </div>
        ) : (
          <div className="max-h-[430px] space-y-2 overflow-y-auto pr-1">
            {relatedDocuments.slice(0, 30).map(document => {
              const summary = document.analysis?.summary ?? document.preview_text
              const type = document.analysis?.document_type
                ?? document.extension?.replace('.', '').toUpperCase()
                ?? 'Document'
              return (
                <article
                  key={document.id}
                  className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/40"
                >
                  <div className="flex items-start gap-2">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-xs font-semibold text-slate-800 dark:text-slate-100">
                        {document.analysis?.title || document.original_filename}
                      </p>
                      <p className="mt-0.5 break-all text-[10px] text-slate-400">{document.relative_path}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">{type}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">
                      {formatBytes(document.byte_size)}
                    </span>
                    {document.extraction_status === 'completed' ? (
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                        {t('knowledge.textAvailable')}
                      </span>
                    ) : (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                        {t('knowledge.originalOnly')}
                      </span>
                    )}
                    {document.explicitly_linked && (
                      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                        {document.explicit_links[0]?.state === 'confirmed'
                          ? document.explicit_links[0]?.prompt_version?.includes('full-content')
                            ? 'Full-content AI link'
                            : 'Linked by AI'
                          : 'Possible match'}
                        {document.explicit_links[0]?.confidence != null
                          ? ` · ${Math.round(document.explicit_links[0].confidence * 100)}%`
                          : ''}
                      </span>
                    )}
                  </div>
                  {summary && (
                    <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-[11px] leading-5 text-slate-600 dark:text-slate-300">
                      {summary}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[10px] text-slate-400">{document.match_reasons.join(' · ')}</p>
                    <div className="flex items-center gap-2">
                      {canEdit && document.explicit_links[0] && (
                        <button
                          className="text-[10px] font-medium text-red-600 hover:underline dark:text-red-300"
                          disabled={flaggingDocumentLinkId === document.explicit_links[0].id}
                          onClick={() => onFlagDocumentLink(document, document.explicit_links[0].id)}
                        >
                          {t('knowledge.flagLink')}
                        </button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onOpenDocument(document)}
                        disabled={document.quarantine !== 'clear'}
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> {t('knowledge.openOriginal')}
                      </Button>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>

      <div>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {t('knowledge.vesselInstallations')}
        </p>
        <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
          {system.installations.map(installation => (
            <Link
              key={installation.id}
              to={`/projects/${installation.project_id}/systems/${installation.id}`}
              className="block rounded-lg border border-slate-200 bg-white p-2.5 hover:border-sky-300 hover:bg-sky-50/40 dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-sky-800 dark:hover:bg-sky-950/20"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-sky-700 dark:text-sky-400">
                    {labelOf(installation.hull_number)}
                  </p>
                  <p className="truncate text-[10px] text-slate-400">
                    {labelOf(installation.vessel_name)} · {labelOf(installation.class_society)}
                  </p>
                </div>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
              </div>
              <p className="mt-1 truncate text-[11px] text-slate-600 dark:text-slate-300">
                {labelOf(installation.system_name || installation.system_description)}
              </p>
              {installation.maker_conflict && (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <p
                    className="text-[10px] text-amber-700 dark:text-amber-300"
                    title={installation.maker_resolution_reasoning ?? undefined}
                  >
                    Recorded {installation.recorded_canonical_vendor ?? installation.vendor_name_raw ?? 'unknown'}
                    {' → '}product maker {installation.canonical_vendor ?? system.maker ?? 'unresolved'}
                    {installation.maker_resolution_confidence != null
                      ? ` (${Math.round(installation.maker_resolution_confidence * 100)}%)`
                      : ''}
                  </p>
                  {canEdit && (
                    <button
                      className="text-[9px] font-medium text-red-600 hover:underline dark:text-red-300"
                      disabled={makerFlaggingId === installation.id}
                      onClick={event => {
                        event.preventDefault()
                        event.stopPropagation()
                        onFlagMaker(installation)
                      }}
                    >
                      {t('knowledge.flagMaker')}
                    </button>
                  )}
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1">
                <Presence yes={installation.has_ra}>RA</Presence>
                <Presence yes={installation.has_inventory}>{t('projectDetail.inventory')}</Presence>
                <Presence yes={installation.has_contacts}>{t('nav.contacts')}</Presence>
                <Presence yes={installation.evidence_count > 0}>{t('knowledge.evidence')}</Presence>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export function KnowledgeWorkspacePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const canEditWorkspace = canManage(user)
  const { success, error: showError } = useToast()
  const [tab, setTab] = useState<WorkspaceTab>('systems')
  const [tree, setTree] = useState<WorkspaceTree | null>(null)
  const [documents, setDocuments] = useState<WorkspaceDocument[]>([])
  const [relatedDocuments, setRelatedDocuments] = useState<RelatedWorkspaceDocument[]>([])
  const [loadingRelatedDocuments, setLoadingRelatedDocuments] = useState(false)
  const [ingest, setIngest] = useState<DocumentIngestStatus | null>(null)
  const [mappingItems, setMappingItems] = useState<DocumentMappingReviewItem[]>([])
  const [mappingTotal, setMappingTotal] = useState(0)
  const [mappingPage, setMappingPage] = useState(1)
  const [mappingStatus, setMappingStatus] = useState<'all' | 'proposed' | 'unmatched'>('all')
  const [mappingTarget, setMappingTarget] = useState<Record<string, string>>({})
  const [assistant, setAssistant] = useState<WorkspaceAssistantAnswer | null>(null)
  const [asking, setAsking] = useState(false)
  const [query, setQuery] = useState(() =>
    new URLSearchParams(window.location.search).get('q') ?? '')
  const [smartResultIds, setSmartResultIds] = useState<string[]>(readSmartResultIds)
  const [functionCode, setFunctionCode] = useState('all')
  const [typeName, setTypeName] = useState('all')
  const [selectedKey, setSelectedKey] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('system'))
  const [loading, setLoading] = useState(true)
  const [workingId, setWorkingId] = useState<string | null>(null)

  const loadTree = useCallback(async () => {
    const data = await knowledgeWorkspaceApi.tree()
    setTree(data)
    const params = new URLSearchParams(window.location.search)
    const requestedSystem = params.get('system')
    const requestedInstallation = params.get('installation')
    const requested = requestedSystem
      ? data.systems.find(system => system.key === requestedSystem)
      : requestedInstallation
        ? data.systems.find(system =>
          system.installations.some(installation => installation.id === requestedInstallation))
        : null
    if (requested) {
      setFunctionCode(requested.function_code)
      setTypeName(requested.type_name)
      setSelectedKey(requested.key)
      return
    }
    setSelectedKey(current =>
      current && data.systems.some(system => system.key === current)
        ? current
        : null)
  }, [])

  const loadDocuments = useCallback(async () => {
    setDocuments(await knowledgeWorkspaceApi.documents())
  }, [])

  const loadIngest = useCallback(async () => {
    setIngest(await knowledgeWorkspaceApi.ingestStatus())
  }, [])

  const loadMapping = useCallback(async (
    page = 1,
    status: 'all' | 'proposed' | 'unmatched' = 'all',
    search = '',
  ) => {
    const data = await knowledgeWorkspaceApi.mappingReview({ page, status, query: search })
    setMappingItems(data.items)
    setMappingTotal(data.total)
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      await Promise.all([loadTree(), loadDocuments(), loadIngest(), loadMapping(1, 'all')])
    } catch (err: any) {
      showError(t('knowledge.errors.loadWorkspace'), err?.message ?? t('errors.unknownError'))
    } finally {
      setLoading(false)
    }
  }, [loadDocuments, loadIngest, loadMapping, loadTree, showError, t])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  useEffect(() => {
    const urlQuery = searchParams.get('q') ?? ''
    if (urlQuery !== query) setQuery(urlQuery)
    if (!tree) return
    const requestedSystem = searchParams.get('system')
    const requestedInstallation = searchParams.get('installation')
    const requested = requestedSystem
      ? tree.systems.find(system => system.key === requestedSystem)
      : requestedInstallation
        ? tree.systems.find(system =>
          system.installations.some(installation => installation.id === requestedInstallation))
        : null
    if (requested && requested.key !== selectedKey) {
      setFunctionCode(requested.function_code)
      setTypeName(requested.type_name)
      setSelectedKey(requested.key)
    }
  }, [query, searchParams, selectedKey, tree])

  const activeIngestJob = ingest?.jobs.find(job => job.status === 'queued' || job.status === 'running') ?? null
  const latestIngestStatus = ingest?.jobs[0]?.status ?? null

  useEffect(() => {
    if (!activeIngestJob) return
    const timer = window.setInterval(() => {
      void Promise.all([loadIngest(), loadDocuments(), loadTree(), loadMapping(mappingPage, mappingStatus, query)])
    }, 5_000)
    return () => window.clearInterval(timer)
  }, [activeIngestJob, loadDocuments, loadIngest, loadMapping, loadTree, mappingPage, mappingStatus, query])

  useEffect(() => {
    if (tab !== 'mapping') return
    const timer = window.setTimeout(() => {
      void loadMapping(mappingPage, mappingStatus, query).catch(err =>
        showError(t('knowledge.errors.loadMappings'), err?.message ?? t('errors.unknownError')))
    }, 250)
    return () => window.clearTimeout(timer)
  }, [loadMapping, mappingPage, mappingStatus, query, showError, t, tab])

  const filteredSystems = useMemo(() => {
    const term = query.trim().toLowerCase()
    const smartIds = new Set(smartResultIds)
    return (tree?.systems ?? []).filter(system => {
      if (!term && functionCode !== 'all' && system.function_code !== functionCode) return false
      if (!term && typeName !== 'all' && system.type_name !== typeName) return false
      if (term && smartResultIds.length) {
        return system.installations.some(installation => smartIds.has(installation.id))
      }
      if (!term) return true
      return [
        system.name,
        system.type_name,
        system.maker,
        system.function_label,
        ...system.aliases,
        ...system.vendors,
        ...system.models,
        ...system.installations.flatMap(item => [
          item.hull_number ?? '',
          item.vessel_name ?? '',
          item.system_name ?? '',
          item.system_description ?? '',
        ]),
      ].join(' ').toLowerCase().includes(term)
    })
  }, [functionCode, query, smartResultIds, tree, typeName])

  const typeFolders = useMemo(() => {
    const counts = new Map<string, { count: number; installations: number; documents: number }>()
    for (const system of tree?.systems ?? []) {
      if (functionCode !== 'all' && system.function_code !== functionCode) continue
      const current = counts.get(system.type_name) ?? { count: 0, installations: 0, documents: 0 }
      current.count++
      current.installations += system.installation_count
      current.documents += system.linked_document_count
      counts.set(system.type_name, current)
    }
    return [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, counts]) => ({ name, ...counts }))
  }, [functionCode, tree])

  const currentFunction = tree?.functions.find(fn => fn.code === functionCode) ?? null
  const directoryLevel = query.trim()
    ? 'search'
    : functionCode === 'all'
      ? 'functions'
      : typeName === 'all'
        ? 'types'
        : 'models'

  const replaceWorkspaceUrl = (changes: {
    q?: string | null
    system?: string | null
    installation?: string | null
    source?: string | null
  }) => {
    const next = new URLSearchParams(window.location.search)
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setSearchParams(next, { replace: true })
  }

  const changeLibraryQuery = (value: string) => {
    setQuery(value)
    setSmartResultIds([])
    sessionStorage.removeItem(SMART_RESULTS_KEY)
    setSelectedKey(null)
    replaceWorkspaceUrl({ q: value || null, system: null, installation: null, source: null })
  }

  const selectLibrarySystem = (key: string) => {
    setSelectedKey(key)
    replaceWorkspaceUrl({ system: key, installation: null })
  }

  const openLibraryRoot = () => {
    setQuery('')
    setFunctionCode('all')
    setTypeName('all')
    setSelectedKey(null)
    setSmartResultIds([])
    replaceWorkspaceUrl({ q: null, system: null, installation: null, source: null })
  }

  const openFunctionFolder = (code: string) => {
    setQuery('')
    setFunctionCode(code)
    setTypeName('all')
    setSelectedKey(null)
    setSmartResultIds([])
    replaceWorkspaceUrl({ q: null, system: null, installation: null, source: null })
  }

  const openTypeFolder = (name: string) => {
    setQuery('')
    setTypeName(name)
    setSelectedKey(null)
    setSmartResultIds([])
    replaceWorkspaceUrl({ q: null, system: null, installation: null, source: null })
  }

  const selectedSystem = useMemo(
    () => tree?.systems.find(system => system.key === selectedKey) ?? null,
    [selectedKey, tree],
  )

  useEffect(() => {
    let cancelled = false
    if (!selectedSystem) {
      setRelatedDocuments([])
      return
    }
    setLoadingRelatedDocuments(true)
    void knowledgeWorkspaceApi.relatedDocuments(selectedSystem)
      .then(result => {
        if (!cancelled) setRelatedDocuments(result)
      })
      .catch((err: any) => {
        if (!cancelled) {
          setRelatedDocuments([])
          showError(t('knowledge.errors.loadRelated'), err?.message ?? t('errors.unknownError'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRelatedDocuments(false)
      })
    return () => { cancelled = true }
  }, [latestIngestStatus, selectedSystem, showError, t])

  const filteredDocuments = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return documents
    return documents.filter(document =>
      [document.original_filename, document.relative_path, document.preview_text, JSON.stringify(document.analysis ?? {})]
        .filter(Boolean).join(' ').toLowerCase().includes(term))
  }, [documents, query])

  const mappingTargets = useMemo(() => {
    const targets: Array<{ value: string; label: string }> = []
    const seenCanonical = new Set<string>()
    for (const system of tree?.systems ?? []) {
      if (system.canonical_system_id && !seenCanonical.has(system.canonical_system_id)) {
        seenCanonical.add(system.canonical_system_id)
        targets.push({
          value: `canonical_system:${system.canonical_system_id}`,
          label: `Product · ${system.name} · ${system.maker ?? 'maker unresolved'} · ${system.canonical_system_id.slice(0, 8)}`,
        })
      }
      for (const installation of system.installations) {
        targets.push({
          value: `project_system:${installation.id}`,
          label: `Vessel · ${installation.hull_number ?? installation.vessel_name ?? 'unknown'} · ${installation.system_name ?? system.name} · ${installation.id.slice(0, 8)}`,
        })
      }
    }
    return targets
  }, [tree])

  const decideDocumentMapping = async (item: DocumentMappingReviewItem, action: 'confirm' | 'reject') => {
    if (!item.link_id) return
    setWorkingId(`mapping:${item.link_id}`)
    try {
      if (action === 'confirm') await knowledgeWorkspaceApi.confirmDocumentLink(item.link_id)
      else await knowledgeWorkspaceApi.rejectDocumentLink(item.link_id)
      success(action === 'confirm' ? t('knowledge.mappingConfirmed') : t('knowledge.mappingRejected'), item.original_filename)
      await Promise.all([loadMapping(mappingPage, mappingStatus, query), loadTree()])
    } catch (err: any) {
      showError(t('knowledge.errors.saveMapping'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const reassignDocumentMapping = async (item: DocumentMappingReviewItem) => {
    const selected = mappingTargets.find(target =>
      target.label === mappingTarget[item.document_object_id])
    if (!selected) return
    const [targetType, targetId] = selected.value.split(':')
    if (!targetId || (targetType !== 'canonical_system' && targetType !== 'project_system')) return
    setWorkingId(`mapping:${item.document_object_id}`)
    try {
      await knowledgeWorkspaceApi.reassignDocument(item.document_object_id, targetType, targetId)
      success(t('knowledge.documentReassigned'), item.original_filename)
      setMappingTarget(current => {
        const next = { ...current }
        delete next[item.document_object_id]
        return next
      })
      await Promise.all([loadMapping(mappingPage, mappingStatus, query), loadTree()])
    } catch (err: any) {
      showError(t('knowledge.errors.reassign'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const flagSystem = async (system: WorkspaceSystem) => {
    if (!system.canonical_system_id) return
    const reason = window.prompt(
      t('knowledge.prompts.groupingWrong', { name: system.name, maker: system.maker ?? t('knowledge.makerUnresolved') }),
      '',
    )
    if (reason === null) return
    setWorkingId(`flag:${system.canonical_system_id}`)
    try {
      const result = await knowledgeWorkspaceApi.flagCanonicalProduct(
        system.canonical_system_id,
        reason,
      )
      success(t('knowledge.groupingFlagged'), t('knowledge.groupingLinksRemoved', { count: result.memberships_flagged }))
      await loadTree()
    } catch (err: any) {
      showError(t('knowledge.errors.flagGrouping'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const flagMakerResolution = async (installation: WorkspaceSystem['installations'][number]) => {
    const reason = window.prompt(
      t('knowledge.prompts.makerWrong', { name: installation.system_name ?? t('knowledge.thisInstallation') }),
      '',
    )
    if (reason === null) return
    setWorkingId(`flag-maker:${installation.id}`)
    try {
      await knowledgeWorkspaceApi.flagMakerResolution(installation.id, reason)
      success(t('knowledge.makerFlagged'), t('knowledge.vendorRestored'))
      await loadTree()
    } catch (err: any) {
      showError(t('knowledge.errors.flagMaker'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const flagDocumentLink = async (document: RelatedWorkspaceDocument, linkId: string) => {
    const reason = window.prompt(t('knowledge.prompts.documentWrong', { filename: document.original_filename }), '')
    if (reason === null) return
    setWorkingId(`flag-document:${linkId}`)
    try {
      await knowledgeWorkspaceApi.flagDocumentLink(linkId, reason)
      success(t('knowledge.documentLinkFlagged'), t('knowledge.linkRemoved'))
      if (selectedSystem) {
        setRelatedDocuments(await knowledgeWorkspaceApi.relatedDocuments(selectedSystem))
      }
    } catch (err: any) {
      showError(t('knowledge.errors.flagDocument'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const updateDump = async () => {
    if (!window.confirm(
      t('knowledge.confirm.updateDump'),
    )) return
    setWorkingId('ingest:update')
    try {
      const job = await knowledgeWorkspaceApi.updateDump(100)
      success(
        t('knowledge.dumpUpdateQueued'),
        t('knowledge.jobScanDetail', { id: job.id.slice(0, 8) }),
      )
      await loadIngest()
      setTab('mapping')
    } catch (err: any) {
      showError(t('knowledge.errors.queueUpdate'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const queueIngest = async (mode: 'inventory' | 'copy' | 'extract' | 'analyze') => {
    if (mode === 'copy' && !window.confirm(
      t('knowledge.confirm.copyDump'),
    )) return
    if (mode === 'analyze' && !window.confirm(
      t('knowledge.confirm.analyze'),
    )) return
    setWorkingId(`ingest:${mode}`)
    try {
      const job = await knowledgeWorkspaceApi.queueIngest(
        mode,
        mode === 'analyze'
          ? { sample_limit: 10, max_cost_usd: 1, confirmed: true }
          : mode === 'extract'
            ? { sample_limit: 25 }
            : undefined,
      )
      success(
        mode === 'inventory'
          ? t('knowledge.inventoryQueued')
          : mode === 'copy'
            ? t('knowledge.copyQueued')
            : mode === 'extract'
              ? t('knowledge.extractionQueued')
              : t('knowledge.aiQueued'),
        t('knowledge.jobSurvivesRestart', { id: job.id.slice(0, 8) }),
      )
      await loadIngest()
      setTab('documents')
    } catch (err: any) {
      showError(t('knowledge.errors.queueJob'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const organizeDocumentBatch = async () => {
    if (!window.confirm(
      t('knowledge.confirm.organize'),
    )) return
    setWorkingId('ingest:organize')
    try {
      const job = await knowledgeWorkspaceApi.organizeDocuments(50, 5)
      success(t('knowledge.organizationQueued'), t('knowledge.jobReadDetail', { id: job.id.slice(0, 8) }))
      await loadIngest()
      setTab('documents')
    } catch (err: any) {
      showError(t('knowledge.errors.queueOrganization'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const cancelIngest = async () => {
    if (!activeIngestJob) return
    setWorkingId('ingest:cancel')
    try {
      await knowledgeWorkspaceApi.cancelIngest(activeIngestJob.id)
      success(t('knowledge.jobCancelled'), t('knowledge.jobCancelledDetail'))
      await loadIngest()
    } catch (err: any) {
      showError(t('knowledge.errors.cancelJob'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const openDocument = async (document: { id: string }) => {
    setWorkingId(`document:${document.id}`)
    try {
      const signed = await knowledgeWorkspaceApi.signDocument(document.id)
      window.open(signed.url, '_blank', 'noopener,noreferrer')
    } catch (err: any) {
      showError(t('knowledge.errors.openDocument'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const extractRelatedDocuments = async (matched: RelatedWorkspaceDocument[]) => {
    const objectIds = matched
      .filter(document => !document.extraction_status)
      .map(document => document.id)
      .slice(0, 25)
    if (!objectIds.length) return
    setWorkingId('extract-related')
    try {
      const job = await knowledgeWorkspaceApi.extractRelatedDocuments(objectIds)
      success(
        'Document information queued',
        `Extracting ${objectIds.length} matched original(s) in job ${job.id.slice(0, 8)}.`,
      )
      await loadIngest()
    } catch (err: any) {
      showError(t('knowledge.errors.extractRelated'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const analyzeRelatedDocuments = async (matched: RelatedWorkspaceDocument[]) => {
    const objectIds = matched
      .filter(document =>
        document.extraction_status === 'completed' && document.analysis_status !== 'completed')
      .map(document => document.id)
      .slice(0, 10)
    if (!objectIds.length) return
    if (!window.confirm(
      t('knowledge.confirm.summarize', { count: objectIds.length }),
    )) return
    setWorkingId('analyze-related')
    try {
      const job = await knowledgeWorkspaceApi.analyzeRelatedDocuments(objectIds, 2)
      success(
        'Document summaries queued',
        `Analyzing ${objectIds.length} matched document(s) in job ${job.id.slice(0, 8)}.`,
      )
      await loadIngest()
    } catch (err: any) {
      showError(t('knowledge.errors.summarize'), err?.message ?? t('errors.unknownError'))
    } finally {
      setWorkingId(null)
    }
  }

  const askAssistant = async () => {
    const question = query.trim()
    if (question.length < 3) return
    setAsking(true)
    try {
      setAssistant(await knowledgeWorkspaceApi.askAssistant(question))
    } catch (err: any) {
      showError(t('knowledge.errors.assistant'), err?.message ?? t('errors.unknownError'))
    } finally {
      setAsking(false)
    }
  }

  const openAssistantCitation = async (citation: WorkspaceAssistantAnswer['citations'][number]) => {
    if (citation.source_type === 'system' && citation.route) {
      navigate(citation.route)
      return
    }
    if (citation.source_type === 'document') {
      setWorkingId(`citation:${citation.source_id}`)
      try {
        const signed = await knowledgeWorkspaceApi.signDocument(citation.source_id)
        window.open(signed.url, '_blank', 'noopener,noreferrer')
      } catch (err: any) {
        showError(t('knowledge.errors.openCitation'), err?.message ?? t('errors.unknownError'))
      } finally {
        setWorkingId(null)
      }
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex max-w-[1800px] flex-wrap items-center gap-3 px-4 py-3">
          <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')} title={t('nav.dashboard')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="mr-2 min-w-0">
            <h1 className="flex items-center gap-2 text-base font-semibold">
              <Library className="h-5 w-5 text-sky-600" /> {t('knowledge.title')}
            </h1>
            <p className="text-[11px] text-slate-400">{t('knowledge.subtitle')}</p>
          </div>

          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={event => changeLibraryQuery(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Escape') changeLibraryQuery('')
                if (event.key === 'Enter' && filteredSystems.length === 1) {
                  selectLibrarySystem(filteredSystems[0].key)
                }
              }}
              aria-label={t('knowledge.searchAria')}
              placeholder={tab === 'documents' || tab === 'mapping'
                ? t('knowledge.searchDocuments')
                : t('knowledge.searchSystems')}
              className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-9 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-800 dark:focus:ring-sky-950"
            />
            {query && (
              <button onClick={() => changeLibraryQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            size="sm"
            loading={asking}
            disabled={query.trim().length < 3}
            onClick={() => void askAssistant()}
            title={t('knowledge.askAiTitle')}
          >
            <Sparkles className="h-4 w-4" /> {t('knowledge.askAi')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void loadAll()} disabled={loading} title={t('common.refresh')}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-[1800px] px-4 py-3">
        {tree && (
          <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs dark:border-slate-800 dark:bg-slate-900">
            <span><strong className="text-slate-800 dark:text-slate-100">{tree.grain.catalog_systems.toLocaleString()}</strong> {t('knowledge.searchableSystems')}</span>
            <span><strong className="text-slate-800 dark:text-slate-100">{tree.grain.vessels}</strong> {t('knowledge.vessels')}</span>
            <span><strong className="text-slate-800 dark:text-slate-100">{Number(ingest?.totals.objects ?? 0).toLocaleString()}</strong> {t('knowledge.copiedDocuments')}</span>
            <span className="text-slate-400">{t('knowledge.selectMatchingModel')}</span>
          </div>
        )}

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div role="tablist" aria-label={t('knowledge.librarySections')} className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            {([
              ['systems', t('knowledge.title'), Library],
              ['documents', t('knowledge.allDocuments'), FileText],
              ['mapping', `${t('knowledge.documentMapping')}${mappingTotal ? ` (${mappingTotal})` : ''}`, Inbox],
            ] as const).map(([value, label, Icon]) => (
              <button
                key={value}
                onClick={() => setTab(value)}
                role="tab"
                aria-selected={tab === value}
                className={`flex items-center gap-1.5 border-r border-slate-200 px-3 py-1.5 text-xs font-medium last:border-r-0 dark:border-slate-700 ${
                  tab === value
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                    : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">
            {t('knowledge.recordsPreserved')}
          </p>
        </div>

        {assistant && (
          <section className="mb-3 rounded-xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-900 dark:bg-violet-950/25">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-sm font-semibold text-violet-900 dark:text-violet-100">{t('knowledge.assistant')}</h2>
                  <button onClick={() => setAssistant(null)} className="text-violet-400 hover:text-violet-700">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">
                  {assistant.answer}
                </p>
                {assistant.citations.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {assistant.citations.map((citation, index) => (
                      <button
                        key={`${citation.source_type}:${citation.source_id}:${index}`}
                        onClick={() => void openAssistantCitation(citation)}
                        disabled={workingId === `citation:${citation.source_id}`}
                        title={citation.quote || citation.fact}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-violet-200 bg-white px-2 py-1 text-left text-[10px] text-violet-800 hover:border-violet-400 dark:border-violet-800 dark:bg-slate-900 dark:text-violet-200"
                      >
                        {citation.source_type === 'document'
                          ? <FileText className="h-3 w-3 shrink-0" />
                          : <Database className="h-3 w-3 shrink-0" />}
                        <span className="truncate">{citation.label}</span>
                        {citation.page != null && <span className="shrink-0 text-violet-400">p.{citation.page}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {assistant.limitations.length > 0 && (
                  <p className="mt-2 text-[10px] text-slate-500 dark:text-slate-400">
                    {t('knowledge.limits')}: {assistant.limitations.join(' · ')}
                  </p>
                )}
                <p className="mt-1 text-[9px] text-violet-400">
                  {t('knowledge.citedAnswer')}
                </p>
              </div>
            </div>
          </section>
        )}

        {loading && !tree ? (
          <div className="py-24 text-center text-sm text-slate-400">{t('knowledge.buildingLibrary')}</div>
        ) : tab === 'systems' ? (
          <div className="grid min-h-[720px] grid-cols-1 overflow-hidden rounded-xl border border-slate-200 bg-white lg:grid-cols-[220px_minmax(360px,0.8fr)_minmax(480px,1.2fr)] dark:border-slate-800 dark:bg-slate-900">
            <aside className="border-b border-slate-200 p-2 lg:border-b-0 lg:border-r dark:border-slate-800">
              <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t('knowledge.folders')}</p>
              <button
                onClick={openLibraryRoot}
                aria-pressed={functionCode === 'all'}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${
                  functionCode === 'all' ? 'bg-sky-50 font-medium text-sky-700 dark:bg-sky-950/40 dark:text-sky-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <Folder className="h-3.5 w-3.5" />
                <span className="min-w-0 flex-1">{t('knowledge.title')}</span>
                <span className="tabular-nums text-slate-400">{tree?.grain.catalog_systems ?? 0}</span>
              </button>
              <button onClick={() => setTab('documents')} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800">
                <FileText className="h-3.5 w-3.5" />
                <span className="min-w-0 flex-1">{t('knowledge.copiedDocuments')}</span>
                <span className="tabular-nums text-slate-400">{documents.length}</span>
              </button>

              <p className="px-2 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t('knowledge.e26Functions')}</p>
              <div className="max-h-[520px] space-y-0.5 overflow-y-auto pr-1">
                {tree?.functions.map(fn => (
                  <button
                    key={fn.code}
                    onClick={() => openFunctionFolder(fn.code)}
                    aria-pressed={functionCode === fn.code}
                    className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs ${
                      functionCode === fn.code
                        ? 'bg-sky-50 font-medium text-sky-700 dark:bg-sky-950/40 dark:text-sky-300'
                        : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Folder className="mt-0.5 h-3.5 w-3.5 shrink-0 fill-amber-200 text-amber-500 dark:fill-amber-900" />
                    <span className="min-w-0 flex-1 leading-4">{fn.label}</span>
                    <span className="tabular-nums text-slate-400">{fn.system_count}</span>
                  </button>
                ))}
              </div>
              {functionCode !== 'all' && typeFolders.length > 0 && (
                <>
                  <p className="px-2 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {t('knowledge.functionalTypes')}
                  </p>
                  <div className="max-h-52 space-y-0.5 overflow-y-auto pr-1">
                    <button
                      onClick={() => openFunctionFolder(functionCode)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${
                        typeName === 'all'
                          ? 'bg-violet-50 font-medium text-violet-700 dark:bg-violet-950/40 dark:text-violet-300'
                          : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Folder className="h-3.5 w-3.5" />
                      <span className="min-w-0 flex-1">{t('knowledge.allTypes')}</span>
                      <span className="text-slate-400">{typeFolders.reduce((sum, item) => sum + item.count, 0)}</span>
                    </button>
                    {typeFolders.map(type => (
                      <button
                        key={type.name}
                        onClick={() => openTypeFolder(type.name)}
                        title={type.name}
                        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${
                          typeName === type.name
                            ? 'bg-violet-50 font-medium text-violet-700 dark:bg-violet-950/40 dark:text-violet-300'
                            : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`}
                      >
                        <Folder className="h-3.5 w-3.5 shrink-0 fill-amber-200 text-amber-500 dark:fill-amber-900" />
                        <span className="min-w-0 flex-1 truncate">{type.name}</span>
                        <span className="text-slate-400">{type.count}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </aside>

            <main className="min-w-0 border-b border-slate-200 lg:border-b-0 lg:border-r dark:border-slate-800">
              <div className="flex min-h-12 items-center justify-between gap-3 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
                <nav aria-label={t('knowledge.currentFolder')} className="flex min-w-0 items-center gap-1 text-sm">
                  <button
                    onClick={openLibraryRoot}
                    className="rounded px-1.5 py-1 font-medium text-sky-700 hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:text-sky-400 dark:hover:bg-sky-950/40"
                  >
                    {t('knowledge.title')}
                  </button>
                  {directoryLevel === 'search' ? (
                    <>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                      <span className="truncate text-slate-500">{t('knowledge.searchResults')}</span>
                    </>
                  ) : (
                    <>
                      {currentFunction && (
                        <>
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                          <button
                            onClick={() => openFunctionFolder(currentFunction.code)}
                            className="truncate rounded px-1.5 py-1 text-sky-700 hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:text-sky-400 dark:hover:bg-sky-950/40"
                          >
                            {currentFunction.label}
                          </button>
                        </>
                      )}
                      {typeName !== 'all' && (
                        <>
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                          <span className="truncate font-medium text-slate-700 dark:text-slate-200">{typeName}</span>
                        </>
                      )}
                    </>
                  )}
                </nav>
                <span aria-live="polite" className="shrink-0 text-[11px] text-slate-400">
                  {directoryLevel === 'functions'
                    ? `${tree?.functions.length ?? 0} folders`
                    : directoryLevel === 'types'
                      ? `${typeFolders.length} folders`
                      : `${filteredSystems.length} model${filteredSystems.length === 1 ? '' : 's'}`}
                </span>
              </div>

              {directoryLevel === 'functions' ? (
                <>
                  <div className="grid grid-cols-[minmax(0,1fr)_90px_110px] border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:border-slate-800 dark:bg-slate-950/40">
                    <span>{t('knowledge.name')}</span><span className="text-right">{t('knowledge.models')}</span><span className="text-right">{t('knowledge.installations')}</span>
                  </div>
                  <div className="max-h-[650px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
                    {tree?.functions.map(fn => (
                      <button
                        key={fn.code}
                        onClick={() => openFunctionFolder(fn.code)}
                        className="grid w-full grid-cols-[minmax(0,1fr)_90px_110px] items-center gap-2 px-3 py-2.5 text-left hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 dark:hover:bg-sky-950/30"
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <Folder className="h-5 w-5 shrink-0 fill-amber-300 text-amber-500 dark:fill-amber-700" />
                          <span className="truncate text-sm font-medium">{fn.label}</span>
                        </span>
                        <span className="text-right text-xs tabular-nums text-slate-500">{fn.system_count}</span>
                        <span className="text-right text-xs tabular-nums text-slate-500">{fn.installation_count}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : directoryLevel === 'types' ? (
                <>
                  <div className="grid grid-cols-[minmax(0,1fr)_70px_90px_100px] border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:border-slate-800 dark:bg-slate-950/40">
                    <span>{t('knowledge.name')}</span><span className="text-right">{t('knowledge.models')}</span><span className="text-right">{t('projectDetail.tabs.documents')}</span><span className="text-right">{t('knowledge.installed')}</span>
                  </div>
                  <div className="max-h-[650px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
                    {typeFolders.map(type => (
                      <button
                        key={type.name}
                        onClick={() => openTypeFolder(type.name)}
                        className="grid w-full grid-cols-[minmax(0,1fr)_70px_90px_100px] items-center gap-2 px-3 py-2.5 text-left hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 dark:hover:bg-sky-950/30"
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <Folder className="h-5 w-5 shrink-0 fill-amber-300 text-amber-500 dark:fill-amber-700" />
                          <span className="truncate text-sm font-medium">{type.name}</span>
                        </span>
                        <span className="text-right text-xs tabular-nums text-slate-500">{type.count}</span>
                        <span className="text-right text-xs tabular-nums text-slate-500">{type.documents}</span>
                        <span className="text-right text-xs tabular-nums text-slate-500">{type.installations}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : filteredSystems.length === 0 ? (
                <div className="flex min-h-64 flex-col items-center justify-center px-6 text-center text-slate-400">
                  <Search className="mb-3 h-8 w-8 text-slate-300 dark:text-slate-600" />
                  <p className="text-sm font-medium">{t('knowledge.noMatchingSystem')}</p>
                  <p className="mt-1 text-xs">{t('knowledge.noMatchingSystemHint')}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-[minmax(0,1fr)_150px_80px_65px] border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:border-slate-800 dark:bg-slate-950/40">
                    <span>{directoryLevel === 'search' ? 'System / model' : 'Model'}</span>
                    <span>{t('fields.maker')}</span><span className="text-right">{t('projectDetail.tabs.documents')}</span><span className="text-right">{t('knowledge.vessels')}</span>
                  </div>
                  <div className="max-h-[650px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
                    {filteredSystems.slice(0, 500).map(system => (
                      <button
                        key={system.key}
                        onClick={() => selectLibrarySystem(system.key)}
                        aria-pressed={selectedKey === system.key}
                        aria-label={`${system.type_name}, model ${system.name}, maker ${system.maker ?? 'unresolved'}`}
                        className={`grid w-full grid-cols-[minmax(0,1fr)_150px_80px_65px] items-center gap-2 px-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 ${
                          selectedKey === system.key
                            ? 'bg-sky-100/80 dark:bg-sky-950/50'
                            : 'hover:bg-sky-50 dark:hover:bg-sky-950/30'
                        }`}
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <Folder className={`h-5 w-5 shrink-0 ${
                            system.status === 'confirmed'
                              ? 'fill-sky-200 text-sky-500 dark:fill-sky-900'
                              : 'fill-slate-200 text-slate-400 dark:fill-slate-700'
                          }`} />
                          <span className="min-w-0">
                            {directoryLevel === 'search' && (
                              <span className="block truncate text-[11px] text-slate-400">{system.type_name}</span>
                            )}
                            <span className="block truncate text-sm font-medium">{system.name}</span>
                          </span>
                        </span>
                        <span className="flex min-w-0 items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                          <span className="truncate">{system.maker ?? 'Unresolved'}</span>
                          {system.maker_conflict && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
                        </span>
                        <span className="text-right text-xs tabular-nums text-slate-500">{system.linked_document_count}</span>
                        <span className="text-right text-xs tabular-nums text-slate-500">{system.vessel_count}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </main>

            <aside className="min-w-0 overflow-y-auto">
              <SystemInspector
                system={selectedSystem}
                canEdit={canEditWorkspace}
                onFlag={system => void flagSystem(system)}
                flagging={!!selectedSystem?.canonical_system_id
                  && workingId === `flag:${selectedSystem.canonical_system_id}`}
                onFlagMaker={installation => void flagMakerResolution(installation)}
                makerFlaggingId={workingId?.startsWith('flag-maker:')
                  ? workingId.slice('flag-maker:'.length)
                  : null}
                relatedDocuments={relatedDocuments}
                loadingDocuments={loadingRelatedDocuments}
                onOpenDocument={document => void openDocument(document)}
                onExtractDocuments={matched => void extractRelatedDocuments(matched)}
                extractingDocuments={workingId === 'extract-related'
                  || activeIngestJob?.mode === 'extract'}
                onAnalyzeDocuments={matched => void analyzeRelatedDocuments(matched)}
                analyzingDocuments={workingId === 'analyze-related'
                  || activeIngestJob?.mode === 'analyze'}
                onFlagDocumentLink={(document, linkId) => void flagDocumentLink(document, linkId)}
                flaggingDocumentLinkId={workingId?.startsWith('flag-document:')
                  ? workingId.slice('flag-document:'.length)
                  : null}
              />
            </aside>
          </div>
        ) : tab === 'mapping' ? (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Inbox className="h-4 w-4 text-amber-500" /> {t('knowledge.mappingReview')}
                </h2>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {t('knowledge.mappingReviewDescription')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={mappingStatus}
                  onChange={event => {
                    setMappingStatus(event.target.value as typeof mappingStatus)
                    setMappingPage(1)
                  }}
                  className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                >
                  <option value="all">{t('knowledge.allReviewItems')}</option>
                  <option value="proposed">{t('knowledge.proposedLinks')}</option>
                  <option value="unmatched">{t('knowledge.unmatchedFiles')}</option>
                </select>
                {canEditWorkspace && !activeIngestJob && (
                  <Button size="sm" loading={workingId === 'ingest:update'} onClick={() => void updateDump()}>
                    <RefreshCw className="h-3.5 w-3.5" /> {t('knowledge.scanMapNewFiles')}
                  </Button>
                )}
              </div>
            </div>
            <datalist id="document-mapping-targets">
              {mappingTargets.map(target => <option key={target.value} value={target.label} />)}
            </datalist>
            {activeIngestJob?.mode === 'update' && (
              <div className="border-b border-sky-100 bg-sky-50 px-4 py-2.5 text-xs text-sky-800 dark:border-sky-950 dark:bg-sky-950/30 dark:text-sky-200">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">
                    {activeIngestJob.notes?.phase ?? activeIngestJob.status} · {activeIngestJob.files_processed}/{activeIngestJob.files_seen}
                  </span>
                  <span className="tabular-nums">${Number(activeIngestJob.cost_usd ?? 0).toFixed(3)} AI cost</span>
                </div>
                <div className="mt-2 h-1 overflow-hidden rounded bg-sky-100 dark:bg-sky-900">
                  <div
                    className="h-full bg-sky-500 transition-all"
                    style={{ width: `${activeIngestJob.files_seen ? Math.min(100, 100 * activeIngestJob.files_processed / activeIngestJob.files_seen) : 2}%` }}
                  />
                </div>
              </div>
            )}
            {mappingItems.length === 0 ? (
              <div className="py-20 text-center">
                <CheckCircle2 className="mx-auto mb-3 h-9 w-9 text-emerald-400" />
                <p className="text-sm font-medium">{t('knowledge.noMappingsReview')}</p>
                <p className="mt-1 text-xs text-slate-400">{t('knowledge.autoConfirmMappings')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {mappingItems.map(item => (
                  <article key={item.link_id ?? `unmatched:${item.document_object_id}`} className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.7fr)]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold" title={item.relative_path}>{item.original_filename}</p>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] dark:bg-slate-800">{item.extraction_status ?? 'not extracted'}</span>
                        {item.confidence != null && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                            {Math.round(Number(item.confidence) * 100)}% confidence
                          </span>
                        )}
                      </div>
                      <p className="mt-1 truncate font-mono text-[9px] text-slate-400">{item.relative_path}</p>
                      <p className="mt-3 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        {item.analysis?.title ?? 'AI title unavailable'}
                      </p>
                      <p className="mt-1 text-[11px] leading-5 text-slate-500 dark:text-slate-400">
                        {item.analysis?.summary ?? item.preview_text ?? 'No readable preview is available.'}
                      </p>
                      {item.citations?.length > 0 && (
                        <div className="mt-3 space-y-1">
                          {item.citations.map((citation, index) => (
                            <blockquote key={`${citation.page}:${index}`} className="border-l-2 border-sky-300 pl-2 text-[10px] text-slate-500">
                              {citation.page != null ? `Page ${citation.page}: ` : ''}“{citation.quote}”
                            </blockquote>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-950/30">
                      {item.link_id ? (
                        <>
                          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{t('knowledge.proposedTarget')}</p>
                          <p className="mt-1 text-sm font-medium">{item.target_label ?? 'Unknown system'}</p>
                          <p className="mt-1 text-[11px] text-slate-500">{item.reasoning ?? 'No reasoning supplied.'}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-[9px] font-semibold uppercase tracking-wide text-red-400">{t('knowledge.noMatch')}</p>
                          <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-300">{item.no_match_reason}</p>
                        </>
                      )}
                      {canEditWorkspace && (
                        <div className="mt-3 space-y-2">
                          <input
                            list="document-mapping-targets"
                            value={mappingTarget[item.document_object_id] ?? ''}
                            onChange={event => setMappingTarget(current => ({
                              ...current,
                              [item.document_object_id]: event.target.value,
                            }))}
                            placeholder={t('knowledge.mappingTargetPlaceholder')}
                            className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                          />
                          <div className="flex flex-wrap gap-2">
                            {item.link_id && (
                              <>
                                <Button
                                  size="sm"
                                  loading={workingId === `mapping:${item.link_id}`}
                                  onClick={() => void decideDocumentMapping(item, 'confirm')}
                                >
                                  <Check className="h-3.5 w-3.5" /> {t('common.confirm')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={workingId === `mapping:${item.link_id}`}
                                  onClick={() => void decideDocumentMapping(item, 'reject')}
                                >
                                  <X className="h-3.5 w-3.5" /> {t('knowledge.reject')}
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!mappingTargets.some(target =>
                                target.label === mappingTarget[item.document_object_id])}
                              loading={workingId === `mapping:${item.document_object_id}`}
                              onClick={() => void reassignDocumentMapping(item)}
                            >
                              {t('knowledge.reassign')}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => void openDocument({ id: item.document_object_id })}>
                              <ExternalLink className="h-3.5 w-3.5" /> {t('knowledge.original')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
            {mappingTotal > 25 && (
              <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs dark:border-slate-800">
                <span>{mappingTotal.toLocaleString()} review items</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={mappingPage === 1} onClick={() => setMappingPage(page => page - 1)}>{t('common.previous')}</Button>
                  <span className="self-center tabular-nums">{t('common.page')} {mappingPage}</span>
                  <Button size="sm" variant="outline" disabled={mappingPage * 25 >= mappingTotal} onClick={() => setMappingPage(page => page + 1)}>{t('common.next')}</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-semibold"><HardDrive className="h-4 w-4 text-sky-500" /> {t('knowledge.copiedDocumentLibrary')}</h2>
                <p className="mt-0.5 text-[11px] text-slate-400">{t('knowledge.copiedDocumentDescription')}</p>
              </div>
              {canEditWorkspace && (
                <div className="flex flex-wrap items-center gap-2">
                  {activeIngestJob ? (
                    <Button size="sm" variant="outline" loading={workingId === 'ingest:cancel'} onClick={() => void cancelIngest()}>
                      {t('common.cancel')} {activeIngestJob.mode}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      loading={workingId === 'ingest:update'}
                      onClick={() => void updateDump()}
                      title={t('knowledge.updateTitle')}
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> {t('knowledge.scanMapNewFiles')}
                    </Button>
                  )}
                </div>
              )}
            </div>
            {canEditWorkspace && !activeIngestJob && (
              <details className="group border-b border-slate-100 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-950/20">
                <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2 text-[11px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 [&::-webkit-details-marker]:hidden">
                  <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
                  <span className="font-medium">{t('knowledge.advancedTools')}</span>
                  <span className="text-slate-400">{t('knowledge.advancedToolsHint')}</span>
                </summary>
                <div className="flex flex-wrap gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-800">
                  <Button size="sm" variant="outline" loading={workingId === 'ingest:inventory'} onClick={() => void queueIngest('inventory')}>
                    {t('knowledge.inventoryInbox')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={Number(ingest?.totals.objects ?? 0) === 0}
                    loading={workingId === 'ingest:extract'}
                    onClick={() => void queueIngest('extract')}
                  >
                    {t('knowledge.extractCount', { count: 25 })}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={Number(ingest?.totals.extracted ?? 0) === 0}
                    loading={workingId === 'ingest:analyze'}
                    onClick={() => void queueIngest('analyze')}
                  >
                    <Sparkles className="h-3.5 w-3.5" /> {t('knowledge.analyzeCount', { count: 10 })}
                  </Button>
                  <Button
                    size="sm"
                    loading={workingId === 'ingest:organize'}
                    onClick={() => void organizeDocumentBatch()}
                  >
                    <Sparkles className="h-3.5 w-3.5" /> {t('knowledge.organizeCount', { count: 50 })}
                  </Button>
                  <Button size="sm" loading={workingId === 'ingest:copy'} onClick={() => void queueIngest('copy')}>
                    {t('knowledge.copyDump')}
                  </Button>
                </div>
              </details>
            )}
            {ingest && (
              <div className="grid gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-2.5 text-xs sm:grid-cols-2 lg:grid-cols-5 dark:border-slate-800 dark:bg-slate-950/30">
                <div>
                  <p className="text-[9px] uppercase text-slate-400">{t('knowledge.sourceRecords')}</p>
                  <p className="font-semibold tabular-nums">{Number(ingest.totals.sources).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-slate-400">{t('knowledge.uniqueObjects')}</p>
                  <p className="font-semibold tabular-nums">{Number(ingest.totals.objects).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-slate-400">{t('knowledge.logicalSize')}</p>
                  <p className="font-semibold tabular-nums">{formatBytes(Number(ingest.totals.logical_bytes))}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-slate-400">{t('knowledge.extractedLinked')}</p>
                  <p className="font-semibold tabular-nums">{ingest.totals.extracted} / {ingest.totals.linked}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-slate-400">{activeIngestJob ? `${activeIngestJob.mode} job` : 'Last job'}</p>
                  <p className={`truncate font-semibold ${
                    activeIngestJob?.status === 'running' ? 'text-sky-600 dark:text-sky-400' : ''
                  }`}>
                    {activeIngestJob
                      ? activeIngestJob.mode === 'update'
                        ? `${activeIngestJob.notes?.phase ?? activeIngestJob.status} · ` +
                          `${Number(activeIngestJob.notes?.new_or_changed ?? activeIngestJob.files_seen)} new/changed · ` +
                          `${activeIngestJob.files_processed}/${activeIngestJob.files_seen}`
                        : `${activeIngestJob.status} · ${activeIngestJob.files_processed}/${activeIngestJob.files_seen}`
                      : ingest.jobs[0]
                        ? ingest.jobs[0].mode === 'update'
                          ? `${ingest.jobs[0].status} · ${Number(ingest.jobs[0].notes?.new_or_changed ?? 0)} new/changed · ` +
                            `${ingest.jobs[0].files_succeeded} mapped` +
                            (ingest.jobs[0].notes?.cost_cap_reached ? ' · processing limit reached; run Update again' : '')
                          : `${ingest.jobs[0].status} · ${ingest.jobs[0].files_succeeded} succeeded`
                        : 'No runs'}
                  </p>
                </div>
                {activeIngestJob && (
                  <div className="sm:col-span-2 lg:col-span-5">
                    <div className="h-1 overflow-hidden rounded bg-slate-200 dark:bg-slate-800">
                      <div
                        className="h-full bg-sky-500 transition-all"
                        style={{ width: `${activeIngestJob.files_seen ? Math.min(100, (100 * activeIngestJob.files_processed) / activeIngestJob.files_seen) : 2}%` }}
                      />
                    </div>
                  </div>
                )}
                {ingest.jobs[0]?.error && (
                  <p className="sm:col-span-2 lg:col-span-5 text-red-600 dark:text-red-400">
                    {ingest.jobs[0].error}
                  </p>
                )}
              </div>
            )}
            {filteredDocuments.length === 0 ? (
              <div className="py-20 text-center">
                <FileText className="mx-auto mb-3 h-9 w-9 text-slate-300 dark:text-slate-600" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('knowledge.noCopiedDocuments')}</p>
                <p className="mx-auto mt-1 max-w-md text-xs text-slate-400">
                  {t('knowledge.contentStoreReady')}
                </p>
              </div>
            ) : (
              <div className="grid gap-3 p-3 sm:grid-cols-2 xl:grid-cols-3">
                {filteredDocuments.map(document => (
                  <article key={document.id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                    <div className="flex items-start gap-2">
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium" title={document.original_filename ?? document.relative_path ?? ''}>
                          {document.original_filename ?? document.relative_path ?? document.sha256.slice(0, 12)}
                        </p>
                        <p className="mt-0.5 truncate font-mono text-[9px] text-slate-400">{document.relative_path}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] dark:bg-slate-800">{formatBytes(Number(document.byte_size))}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] dark:bg-slate-800">{document.extraction_status ?? 'not extracted'}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] dark:bg-slate-800">{document.link_count} links</span>
                    </div>
                    {document.preview_text && <p className="mt-2 line-clamp-3 text-[11px] text-slate-500 dark:text-slate-400">{document.preview_text}</p>}
                    <Button
                      className="mt-3"
                      size="sm"
                      variant="outline"
                      loading={workingId === `document:${document.id}`}
                      disabled={document.quarantine !== 'clear'}
                      onClick={() => void openDocument(document)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> {t('knowledge.openOriginal')}
                    </Button>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
